import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  RefreshCw, Calculator, DollarSign, Search, ChevronDown, ChevronRight,
  X, Download, Loader2, FileText, Lock, Unlock, CheckSquare, RotateCcw, Clock, Ban
} from 'lucide-react';
import ProcessProgressModal from '../components/ProcessProgressModal';
import ResumenLiquidacionModal from '../components/ResumenLiquidacionModal';

// Etiquetas de los esquemas EASY normalizados que devuelve el backend (ver ESQUEMA_TARIFA_CASE_SQL)
const EASY_LABELS = {
  EASY_BOE_5: 'Easy BOE +5',
  EASY_BOE_15: 'Easy BOE +15',
  EASY_BOE_25: 'Easy BOE +25',
  EASY_3_5: 'Easy 3 +5',
  EASY_3_15: 'Easy 3 +15',
  EASY_3_25: 'Easy 3 +25',
  EASY_6_5: 'Easy 6 +5',
  EASY_6_15: 'Easy 6 +15',
  EASY_6_25: 'Easy 6 +25'
};

// Cualquier valor que no sea un esquema técnico conocido es el nombre de un contrato.
// `esquemasVolumen` es el conjunto de esquemas de volumen de CUPS registrados
// (AUREUM/RASTREATOR/...), obtenido de /api/tarifas/comisiones-volumen/matriz.
const getEsquemaTarifaLabel = (esquema, esquemasVolumen) => {
  if (esquema === 'extramargen') return 'Extramargen';
  if (EASY_LABELS[esquema]) return EASY_LABELS[esquema];
  if (esquemasVolumen?.has(esquema)) return `Volumen CUPS: ${esquema}`;
  return `Contrato: ${esquema}`;
};

// Etiquetas de los tipos de job que calculan liquidacion/decomision (ver filtro "Procesada en Job")
const JOB_TIPO_PROCESO_LABELS = {
  liquidacion: 'Liquidación',
  decomision: 'Decomisión',
  liquidacion_batch: 'Liquidación (selección)',
  decomision_batch: 'Decomisión (selección)',
  liquidacion_volumen_cups: 'Liquidación (Volumen CUPS)'
};

// Filtro de rango de fechas compacto: un único control (un solo "hueco") con las
// fechas desde/hasta, en lugar de dos inputs con borde propio y una etiqueta aparte
const DateRangeFilter = ({ label, desde, hasta, onDesdeChange, onHastaChange, titleDesde, titleHasta, className = '' }) => (
  <div className={`flex items-center gap-1 pl-2 pr-1 py-1 border border-gray-300 rounded bg-white ${className}`}>
    <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{label}</span>
    <input
      type="date"
      value={desde}
      onChange={(e) => onDesdeChange(e.target.value)}
      title={titleDesde}
      className="flex-1 min-w-0 text-xs border-0 p-0 focus:ring-0 focus:outline-none bg-transparent text-gray-700"
    />
    <ChevronRight className="h-3 w-3 text-gray-300 shrink-0" />
    <input
      type="date"
      value={hasta}
      onChange={(e) => onHastaChange(e.target.value)}
      title={titleHasta}
      className="flex-1 min-w-0 text-xs border-0 p-0 focus:ring-0 focus:outline-none bg-transparent text-gray-700"
    />
  </div>
);

// Etiquetas de las secciones de columnas, usadas también en el panel de favoritas
const SECTION_LABELS = {
  identificacion: 'Identificación',
  contrato: 'Contrato',
  tecnico: 'Técnico',
  tarifa: 'Tarifa',
  potencias: 'Potencias',
  margenes_energia: 'Márgenes Energía',
  margenes_potencia: 'Márgenes Potencia',
  otras_condiciones: 'Otras Condiciones',
  liquidacion_datos: 'Datos Liquidación',
  liquidacion: 'Liquidación',
  decomision: 'Decomisión'
};

const formatFechaCorta = (value) => value ? new Date(value).toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';

const badgeEstadoContrato = (row) => (
  <span className={`px-1.5 py-0.5 rounded text-xs ${
    row.estado === 'Activo' ? 'bg-green-100 text-green-800' :
    row.estado === 'Anulado' ? 'bg-red-100 text-red-800' :
    'bg-gray-100 text-gray-800'
  }`}>{row.estado || '-'}</span>
);

const badgeEsquema = (valor, esquemasVolumenCups, sinLabel) => valor ? (
  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
    valor === 'extramargen' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
    valor === 'fijo' ? 'bg-green-50 text-green-700 border border-green-200' :
    (valor === 'easy' || valor?.startsWith('EASY_')) ? 'bg-purple-50 text-purple-700 border border-purple-200' :
    esquemasVolumenCups.has(valor) ? 'bg-teal-50 text-teal-700 border border-teal-200' :
    'bg-gray-50 text-gray-700 border border-gray-200'
  }`}>{valor}</span>
) : <span className="text-gray-400 italic text-xs">{sinLabel}</span>;

// Registro de todas las columnas seleccionables como favoritas: clave única, sección de
// origen, etiqueta y función de render reutilizando el mismo formato que las columnas
// originales de cada sección.
const buildFavoritoColumnDefs = (esquemasVolumenCups) => [
  { key: 'identificacion__id', section: 'identificacion', label: 'ID', render: row => row.id || '-' },
  { key: 'identificacion__cups', section: 'identificacion', label: 'CUPS', render: row => row.cups || '-' },
  { key: 'identificacion__identificador', section: 'identificacion', label: 'Identificador', render: row => row.identificador || '-' },
  { key: 'identificacion__nombre_completo', section: 'identificacion', label: 'Cliente', render: row => row.nombre_completo || '-' },
  { key: 'identificacion__tipo_persona', section: 'identificacion', label: 'Tipo Pers.', render: row => row.tipo_persona || '-' },
  { key: 'identificacion__canal', section: 'identificacion', label: 'Canal', render: row => row.canal || '-' },
  { key: 'identificacion__ps_id', section: 'identificacion', label: 'PS ID', render: row => row.ps_id || '-' },

  { key: 'contrato__codigo', section: 'contrato', label: 'Código', render: row => row.codigo || '-' },
  { key: 'contrato__tarifa_atr', section: 'contrato', label: 'Tarifa ATR', render: row => row.tarifa_atr || '-' },
  { key: 'contrato__nombre', section: 'contrato', label: 'Nombre Tarifa', render: row => row.nombre || '-' },
  { key: 'contrato__estado', section: 'contrato', label: 'Estado', render: badgeEstadoContrato },
  { key: 'contrato__subestado', section: 'contrato', label: 'Subestado', render: row => row.subestado || '-' },
  { key: 'contrato__tipo_contrato_atr', section: 'contrato', label: 'Tipo Cont. ATR', render: row => row.tipo_contrato_atr || '-' },
  { key: 'contrato__fecha_inicio', section: 'contrato', label: 'Fec.Inicio', render: row => formatFechaCorta(row.fecha_inicio) },
  { key: 'contrato__fecha_fin', section: 'contrato', label: 'Fec.Fin', render: row => formatFechaCorta(row.fecha_fin) },
  { key: 'contrato__fecha_inicio_producto', section: 'contrato', label: 'Fec.Ini.Prod', render: row => formatFechaCorta(row.fecha_inicio_producto) },
  { key: 'contrato__fecha_fin_producto', section: 'contrato', label: 'Fec.Fin Prod', render: row => formatFechaCorta(row.fecha_fin_producto) },
  { key: 'contrato__fecha_firma', section: 'contrato', label: 'Fec.Firma', render: row => formatFechaCorta(row.fecha_firma) },
  { key: 'contrato__permanencia', section: 'contrato', label: 'Permanencia', render: row => row.permanencia || '-' },
  { key: 'contrato__permanencia_duracion_dias', section: 'contrato', label: 'Perm. Días', align: 'right', render: row => row.permanencia_duracion_dias || '-' },
  { key: 'contrato__fecha_permanencia', section: 'contrato', label: 'Fec.Perm.', render: row => formatFechaCorta(row.fecha_permanencia) },
  { key: 'contrato__fecha_fin_estimada', section: 'contrato', label: 'Fec.Fin Est.', render: row => formatFechaCorta(row.fecha_fin_estimada) },
  { key: 'contrato__fecha_inicio_info', section: 'contrato', label: 'Fec.Ini Info', render: row => formatFechaCorta(row.fecha_inicio_info) },
  { key: 'contrato__updated_at', section: 'contrato', label: 'Updated At', render: row => formatFechaCorta(row.updated_at) },
  { key: 'contrato__created_at', section: 'contrato', label: 'Created At', render: row => formatFechaCorta(row.created_at) },
  { key: 'contrato__updated_at_boe', section: 'contrato', label: 'Updated BOE', render: row => formatFechaCorta(row.updated_at_boe) },

  { key: 'tecnico__codigo_distribuidora', section: 'tecnico', label: 'Cód. Distrib.', render: row => row.codigo_distribuidora || '-' },
  { key: 'tecnico__distribuidora', section: 'tecnico', label: 'Distribuidora', render: row => row.distribuidora || '-' },
  { key: 'tecnico__provincia', section: 'tecnico', label: 'Provincia', render: row => row.provincia || '-' },
  { key: 'tecnico__municipio', section: 'tecnico', label: 'Municipio', render: row => row.municipio || '-' },
  { key: 'tecnico__cnae', section: 'tecnico', label: 'CNAE', render: row => row.cnae || '-' },
  { key: 'tecnico__consumo_anual_estimado', section: 'tecnico', label: 'CAE Anual Est. (kWh)', align: 'right', render: row => row.consumo_anual_estimado != null ? (Number(row.consumo_anual_estimado) / 1000).toFixed(2) : '-' },
  { key: 'tecnico__tipo_autoconsumo', section: 'tecnico', label: 'Tipo Autocons.', render: row => row.tipo_autoconsumo || '-' },
  { key: 'tecnico__fecha_activacion_autoconsumo', section: 'tecnico', label: 'Fec.Act.Autocons.', render: row => formatFechaCorta(row.fecha_activacion_autoconsumo) },
  { key: 'tecnico__tiene_bateria_virtual', section: 'tecnico', label: 'Batería Virtual', render: row => row.tiene_bateria_virtual ? 'Sí' : 'No' },
  { key: 'tecnico__id_bateria', section: 'tecnico', label: 'ID Batería', render: row => row.id_bateria || '-' },
  { key: 'tecnico__carga_bateria', section: 'tecnico', label: 'Carga Batería', render: row => row.carga_bateria || '-' },
  { key: 'tecnico__tipo_mandato', section: 'tecnico', label: 'Tipo Mandato', render: row => row.tipo_mandato || '-' },

  { key: 'tarifa__id_tarifa_precios', section: 'tarifa', label: 'ID Tarifa', render: row => row.id_tarifa_precios || '-' },
  { key: 'tarifa__motivo_precio', section: 'tarifa', label: 'Motivo Precio', render: row => row.motivo_precio || '-' },
  { key: 'tarifa__tipo_tarifa_precio', section: 'tarifa', label: 'Tipo Tarifa', render: row => row.tipo_tarifa_precio || '-' },
  { key: 'tarifa__descripcion_tarifa', section: 'tarifa', label: 'Descripción Tarifa', render: row => row.descripcion_tarifa || '-' },
  { key: 'tarifa__alias_externo', section: 'tarifa', label: 'Alias Externo', render: row => row.alias_externo || '-' },
  { key: 'tarifa__fee_indexado', section: 'tarifa', label: 'Fee Indexado', align: 'right', render: row => row.fee_indexado != null ? Number(row.fee_indexado).toFixed(4) : '-' },
  { key: 'tarifa__esquema_tarifa', section: 'tarifa', label: 'Esquema Tarifa', render: row => badgeEsquema(row.esquema_tarifa, esquemasVolumenCups, 'Sin tarifa') },

  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `potencias__p${n}`, section: 'potencias', label: `P${n} (kW)`, align: 'right', render: row => row[`potencia_contratada_p${n}_kw`] != null ? Number(row[`potencia_contratada_p${n}_kw`]).toFixed(2) : '-' })),

  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `margenes_energia__e${n}_base`, section: 'margenes_energia', label: `E${n} Base`, align: 'right', render: row => row[`e${n}_base`] != null ? Number(row[`e${n}_base`]).toFixed(4) : '-' })),
  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `margenes_energia__ext_e${n}`, section: 'margenes_energia', label: `Ext.P${n} €/kW`, align: 'right', render: row => row[`extramargen_P${n}_euros_kw`] != null ? Number(row[`extramargen_P${n}_euros_kw`]).toFixed(4) : '-' })),
  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `margenes_energia__e${n}_final`, section: 'margenes_energia', label: `E${n} Final`, align: 'right', render: row => row[`e${n}_final`] != null ? Number(row[`e${n}_final`]).toFixed(4) : '-' })),

  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `margenes_potencia__p${n}_base`, section: 'margenes_potencia', label: `P${n} Base`, align: 'right', render: row => row[`p${n}_base`] != null ? Number(row[`p${n}_base`]).toFixed(2) : '-' })),
  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `margenes_potencia__ext_p${n}`, section: 'margenes_potencia', label: `Ext.P${n} €/kW/año`, align: 'right', render: row => row[`extramargen_P${n}_euros_kw_anio`] != null ? Number(row[`extramargen_P${n}_euros_kw_anio`]).toFixed(2) : '-' })),
  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `margenes_potencia__p${n}_final`, section: 'margenes_potencia', label: `P${n} Final`, align: 'right', render: row => row[`p${n}_final`] != null ? Number(row[`p${n}_final`]).toFixed(2) : '-' })),

  { key: 'otras_condiciones__a1_base', section: 'otras_condiciones', label: 'A1 Base', align: 'right', render: row => row.a1_base != null ? Number(row.a1_base).toFixed(2) : '-' },
  { key: 'otras_condiciones__margen_exc', section: 'otras_condiciones', label: 'Margen Exc', align: 'right', render: row => row.margen_exc != null ? Number(row.margen_exc).toFixed(2) : '-' },
  { key: 'otras_condiciones__porc_exc', section: 'otras_condiciones', label: 'Porc. Exc', align: 'right', render: row => row.porc_exc != null ? Number(row.porc_exc).toFixed(2) : '-' },
  { key: 'otras_condiciones__anyos', section: 'otras_condiciones', label: 'Años', align: 'right', render: row => row.anyos || '-' },
  { key: 'otras_condiciones__meses', section: 'otras_condiciones', label: 'Meses', align: 'right', render: row => row.meses || '-' },
  { key: 'otras_condiciones__aplica_limite_servicios_ajustes', section: 'otras_condiciones', label: 'Límite Serv.Ajustes', render: row => row.aplica_limite_servicios_ajustes ? 'Sí' : 'No' },
  { key: 'otras_condiciones__observaciones', section: 'otras_condiciones', label: 'Observaciones', render: row => row.observaciones || '-' },

  { key: 'liquidacion_datos__liquidacion_tipo', section: 'liquidacion_datos', label: 'Tipo Liq.', render: row => row.liquidacion_tipo || '-' },
  { key: 'liquidacion_datos__esquema_liquidacion', section: 'liquidacion_datos', label: 'Esquema Liquidacion', render: row => badgeEsquema(row.esquema_liquidacion, esquemasVolumenCups, 'Sin esquema') },
  { key: 'liquidacion_datos__liquidacion_origen_consumo', section: 'liquidacion_datos', label: 'Origen Consumo', render: row => row.liquidacion_origen_consumo ? (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
      row.liquidacion_origen_consumo === 'SIPS' ? 'bg-green-100 text-green-800' :
      row.liquidacion_origen_consumo === 'CONTRATO' || row.liquidacion_origen_consumo === 'contrato' ? 'bg-blue-100 text-blue-800' :
      row.liquidacion_origen_consumo === 'ESTIMADO' ? 'bg-yellow-100 text-yellow-800' :
      'bg-gray-100 text-gray-800'
    }`}>{row.liquidacion_origen_consumo}</span>
  ) : '-' },
  { key: 'liquidacion_datos__porcentaje_extra_broker', section: 'liquidacion_datos', label: 'Porc.Extra Broker', align: 'right', render: row => row.liquidacion_porcentaje_extra_broker != null ? Number(row.liquidacion_porcentaje_extra_broker).toFixed(4) : '-' },
  { key: 'liquidacion_datos__comision_emwh', section: 'liquidacion_datos', label: 'Com. €/MWh', align: 'right', render: row => row.liquidacion_comision_emwh != null ? Number(row.liquidacion_comision_emwh).toFixed(2) : '-' },
  { key: 'liquidacion_datos__comision_ecups', section: 'liquidacion_datos', label: 'Com. €/CUPS', align: 'right', render: row => row.liquidacion_comision_ecups != null ? Number(row.liquidacion_comision_ecups).toFixed(2) : '-' },
  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `liquidacion_datos__cae_p${n}`, section: 'liquidacion_datos', label: `CAE P${n} (kWh)`, align: 'right', render: row => row[`liquidacion_cae_p${n}`] != null ? (Number(row[`liquidacion_cae_p${n}`]) / 1000).toFixed(2) : '-' })),
  ...[1, 2, 3, 4, 5, 6].map(n => ({ key: `liquidacion_datos__pot_p${n}`, section: 'liquidacion_datos', label: `Pot.P${n} (kW)`, align: 'right', render: row => row[`liquidacion_potencia_contratada_p${n}_kw`] != null ? Number(row[`liquidacion_potencia_contratada_p${n}_kw`]).toFixed(2) : '-' })),
  { key: 'liquidacion_datos__fecha_inicio_producto', section: 'liquidacion_datos', label: 'Fec.Ini.Prod.Liq', render: row => formatFechaCorta(row.liquidacion_fecha_inicio_producto) },
  { key: 'liquidacion_datos__fecha_fin_producto', section: 'liquidacion_datos', label: 'Fec.Fin.Prod.Liq', render: row => formatFechaCorta(row.liquidacion_fecha_fin_producto) },
  { key: 'liquidacion_datos__dias_incluidos', section: 'liquidacion_datos', label: 'Días Incl.', align: 'right', render: row => row.dias_incluidos_liquidacion || '-' },
  { key: 'liquidacion_datos__consumo_anual_estimado', section: 'liquidacion_datos', label: 'Consumo Anual (kWh)', align: 'right', render: row => row.consumo_anual_estimado != null ? Number(row.consumo_anual_estimado / 1000).toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },

  { key: 'liquidacion__estado', section: 'liquidacion', label: 'Estado Liq', render: row => (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
      row.liquidacion_estado === 'calculada' ? 'bg-green-100 text-green-800' :
      row.liquidacion_estado === 'pendiente' ? 'bg-amber-100 text-amber-800' :
      row.liquidacion_estado === 'bloqueada' ? 'bg-red-100 text-red-800' :
      row.liquidacion_estado === 'no_liquidable' ? 'bg-rose-100 text-rose-800' :
      row.liquidacion_estado === 'error' ? 'bg-red-200 text-red-900' :
      'bg-gray-100 text-gray-800'
    }`}>{row.liquidacion_estado || '-'}</span>
  )},
  { key: 'liquidacion__error', section: 'liquidacion', label: 'Error Liq', render: row => row.liquidacion_error || '-' },
  { key: 'liquidacion__valor', section: 'liquidacion', label: 'Liquidación €', align: 'right', render: row => row.liquidacion != null ? `${Number(row.liquidacion).toFixed(2)} €` : '-' },
  { key: 'liquidacion__extramargen', section: 'liquidacion', label: 'Extramargen €', align: 'right', render: row => row.liquidacion_extramargen_bruto != null ? `${Number(row.liquidacion_extramargen_bruto).toFixed(2)} €` : '-' },
  { key: 'liquidacion__fecha_calculo', section: 'liquidacion', label: 'Fec.Cálculo', render: row => formatFechaCorta(row.liquidacion_fecha_calculo) },

  { key: 'decomision__estado', section: 'decomision', label: 'Estado Dec', render: row => (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
      row.decomision_estado === 'calculada' ? 'bg-purple-100 text-purple-800' :
      row.decomision_estado === 'pendiente' ? 'bg-amber-100 text-amber-800' :
      row.decomision_estado === 'bloqueada' ? 'bg-red-100 text-red-800' :
      row.decomision_estado === 'error' ? 'bg-red-200 text-red-900' :
      'bg-gray-100 text-gray-800'
    }`}>{row.decomision_estado || '-'}</span>
  )},
  { key: 'decomision__error', section: 'decomision', label: 'Error Dec', render: row => row.decomision_error || '-' },
  { key: 'decomision__valor', section: 'decomision', label: 'Decomisión €', align: 'right', render: row => row.decomision != null ? `${Number(row.decomision).toFixed(2)} €` : '-' },
  { key: 'decomision__dias', section: 'decomision', label: 'Días Dec', align: 'right', render: row => row.decomision_dias_calculo || '-' },
  { key: 'decomision__fecha_calculo', section: 'decomision', label: 'Fec.Cálculo', render: row => formatFechaCorta(row.decomision_fecha_calculo) }
];

const FAVORITOS_STORAGE_KEY = 'liquidaciones_columnas_favoritas';
const MESES_SYNC_STORAGE_KEY = 'liquidaciones_sync_meses';
const MESES_SYNC_DEFAULT = 40;
const MESES_SYNC_ULTIMO_ANIO = 12;

const LiquidacionesPage = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  // Valor inmediato del input; se propaga a `search` (usado en la query) con debounce
  // para no disparar una petición pesada por cada tecla pulsada
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  const [filters, setFilters] = useState({
    estado_historificacion: '',
    canal: '',
    tarifa_atr: '',
    id_tarifa_precios: '',
    esquema_tarifa: '',
    liquidacion_estado: '',
    decomision_estado: '',
    tipo_persona: '',
    estado: '',
    subestado: '',
    fecha_inicio_desde: '',
    fecha_inicio_hasta: '',
    fecha_fin_desde: '',
    fecha_fin_hasta: '',
    liquidacion_fecha_calculo_desde: '',
    liquidacion_fecha_calculo_hasta: '',
    decomision_fecha_calculo_desde: '',
    decomision_fecha_calculo_hasta: '',
    porcentaje_extra_broker_operador: '=',
    porcentaje_extra_broker_valor: '',
    // Llegada desde el listado de Jobs (enlace "Ver liquidaciones")
    job_id: searchParams.get('job_id') || ''
  });

  // Estado para el modal de progreso
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentProcessName, setCurrentProcessName] = useState('');

  // Estado para el modal de resumen de liquidación
  const [resumenModalOpen, setResumenModalOpen] = useState(false);
  const [resumenData, setResumenData] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [canalParaDesactivar, setCanalParaDesactivar] = useState(null);

  // Estado para selección múltiple
  const [selectedRows, setSelectedRows] = useState(new Set());
  // true cuando la selección actual proviene de "seleccionar todos los resultados del filtro"
  // (no solo la página visible)
  const [todosFiltradosSeleccionados, setTodosFiltradosSeleccionados] = useState(false);
  const [loadingSeleccionarTodos, setLoadingSeleccionarTodos] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const desactivarCanalMutation = useMutation({
    mutationFn: async (nombre_canal) => {
      const response = await axios.post('/api/canales/', { nombre_canal });
      return response.data;
    },
    onSuccess: () => {
      setCanalParaDesactivar(null);
      toast.success('Canal añadido a canales desactivados');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'No se pudo desactivar el canal');
    },
  });

  // Panel de filtros colapsable
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Nodo del submenú (App.jsx) donde se insertan los botones de acción vía portal
  const [navActionsSlot, setNavActionsSlot] = useState(null);
  useEffect(() => {
    setNavActionsSlot(document.getElementById('nav-actions-slot'));
  }, []);

  // Secciones colapsables de columnas
  const [expandedSections, setExpandedSections] = useState({
    favoritas: true,
    identificacion: true,
    contrato: false,
    tecnico: false,
    tarifa: false,
    potencias: false,
    margenes_energia: false,
    margenes_potencia: false,
    otras_condiciones: false,
    liquidacion_datos: false,
    liquidacion: false,
    decomision: false
  });

  // Columnas marcadas como favoritas: se muestran fijas en la sección "Favoritas"
  // a la izquierda de la tabla, independientemente de la sección de origen. Se
  // persisten en localStorage para mantenerlas entre sesiones.
  const [favoriteColumns, setFavoriteColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(FAVORITOS_STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [favoritosPanelOpen, setFavoritosPanelOpen] = useState(false);
  const [favoritosPanelPos, setFavoritosPanelPos] = useState(null);
  const favoritosButtonRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(FAVORITOS_STORAGE_KEY, JSON.stringify([...favoriteColumns]));
  }, [favoriteColumns]);

  const toggleFavoritoColumn = (key) => {
    setFavoriteColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Construye los query params de filtros/búsqueda comunes al listado paginado y al
  // endpoint de ids (usado para poder seleccionar el 100% de los resultados del filtro)
  const buildFiltrosParams = () => new URLSearchParams({
    ...(search && { search }),
    ...Object.fromEntries(
      Object.entries(filters).filter(([k, v]) => {
        if (v === '') return false;
        // El operador solo se envía si hay un valor de % Extra margen a comparar
        if (k === 'porcentaje_extra_broker_operador' && !filters.porcentaje_extra_broker_valor) return false;
        return true;
      })
    )
  });

  // Query para obtener liquidaciones
  const { data: liquidacionesData, isLoading, refetch } = useQuery({
    queryKey: ['liquidaciones', page, pageSize, search, filters],
    queryFn: async () => {
      const params = buildFiltrosParams();
      params.set('page', page.toString());
      params.set('page_size', pageSize.toString());
      const res = await axios.get(`/api/liquidaciones/?${params}`);
      return res.data;
    },
    // Ya existe un botón "Refrescar" manual; evita refetches automáticos (p.ej. al
    // volver a la pestaña) de una query que puede ser costosa en el backend
    staleTime: 30 * 1000
  });

  // Query para obtener los esquemas de tarifa disponibles (para el filtro "Esquema Tarifa")
  const { data: esquemasTarifaData } = useQuery({
    queryKey: ['esquemas-tarifa'],
    queryFn: async () => {
      const res = await axios.get('/api/liquidaciones/esquemas-tarifa/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000
  });

  // Nombres de esquema de volumen de CUPS registrados (AUREUM/RASTREATOR/...), para
  // que getEsquemaTarifaLabel no los etiquete incorrectamente como "Contrato: X"
  const { data: comisionesVolumenData } = useQuery({
    queryKey: ['comisiones-volumen-esquemas'],
    queryFn: async () => {
      const res = await axios.get('/api/tarifas/comisiones-volumen/matriz');
      return res.data;
    },
    staleTime: 5 * 60 * 1000
  });
  const esquemasVolumenCups = new Set(Object.keys(comisionesVolumenData?.esquemas || {}));

  // Definiciones de columnas favoritas (dependen de esquemasVolumenCups para los badges)
  const favoritoColumnDefs = useMemo(() => buildFavoritoColumnDefs(esquemasVolumenCups), [esquemasVolumenCups]);
  const favoritoColumnsMap = useMemo(
    () => Object.fromEntries(favoritoColumnDefs.map(def => [def.key, def])),
    [favoritoColumnDefs]
  );
  const favoritoColumnsBySection = useMemo(() => {
    const grouped = {};
    favoritoColumnDefs.forEach(def => {
      if (!grouped[def.section]) grouped[def.section] = [];
      grouped[def.section].push(def);
    });
    return grouped;
  }, [favoritoColumnDefs]);
  const favoriteKeysOrdered = useMemo(
    () => favoritoColumnDefs.filter(def => favoriteColumns.has(def.key)).map(def => def.key),
    [favoritoColumnDefs, favoriteColumns]
  );

  // Jobs de liquidación/decomisión recientes (para el filtro "Procesada en job")
  const TIPOS_PROCESO_LIQUIDACION_DECOMISION = ['liquidacion', 'decomision', 'liquidacion_batch', 'decomision_batch', 'liquidacion_volumen_cups'];
  const { data: jobsFiltroData } = useQuery({
    queryKey: ['jobs-filtro-liquidaciones'],
    queryFn: async () => {
      const res = await axios.get('/api/jobs/');
      return res.data;
    },
    staleTime: 30 * 1000
  });
  const jobsFiltro = (jobsFiltroData?.data || [])
    .filter((job) => TIPOS_PROCESO_LIQUIDACION_DECOMISION.includes(job.tipo_proceso))
    .slice(0, 20);

  // Si venimos de un enlace con job_id que no está entre los últimos 20, lo pedimos aparte
  // para que el desplegable lo pueda mostrar seleccionado igualmente
  const jobIdEnLista = jobsFiltro.some((job) => job.id === filters.job_id);
  const { data: jobFiltroActualData } = useQuery({
    queryKey: ['job-filtro-actual', filters.job_id],
    queryFn: async () => {
      const res = await axios.get(`/api/jobs/${filters.job_id}`);
      return res.data;
    },
    enabled: !!filters.job_id && !jobIdEnLista,
    staleTime: 60 * 1000
  });
  const jobsFiltroConActual = jobIdEnLista || !jobFiltroActualData?.data
    ? jobsFiltro
    : [jobFiltroActualData.data, ...jobsFiltro];

  // Antigüedad máxima (en meses) de fecha_inicio_producto que trae la sincronización.
  // Editable por el usuario y persistida en localStorage.
  const [mesesSync, setMesesSync] = useState(() => {
    const saved = Number(localStorage.getItem(MESES_SYNC_STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : MESES_SYNC_DEFAULT;
  });

  useEffect(() => {
    localStorage.setItem(MESES_SYNC_STORAGE_KEY, String(mesesSync));
  }, [mesesSync]);

  const handleMesesSyncChange = (e) => {
    const valor = Number(e.target.value);
    setMesesSync(Number.isFinite(valor) && valor > 0 ? Math.floor(valor) : MESES_SYNC_DEFAULT);
  };

  // Mutación para sincronizar historificación (asíncrona con modal)
  // Acepta un número opcional de meses que sobreescribe el valor del input (ej. sync rápido del último año)
  const syncMutation = useMutation({
    mutationFn: async (mesesOverride) => {
      const meses = mesesOverride ?? mesesSync;
      const res = await axios.post('/api/liquidaciones/sync/start', null, {
        params: { meses }
      });
      return res.data;
    },
    onSuccess: (data) => {
      if (data.success && data.job_id) {
        setCurrentJobId(data.job_id);
        setCurrentProcessName('Sincronización de Historificación');
        setProgressModalOpen(true);
        toast.success(data.message);
      } else {
        toast.error('Error iniciando sincronización');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error sincronizando historificación');
    }
  });

  // Mutación para calcular liquidaciones (asíncrona con modal)
  const calcularMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post('/api/liquidaciones/calcular/start?limite=0');
      return res.data;
    },
    onSuccess: (data) => {
      if (data.success && data.job_id) {
        setCurrentJobId(data.job_id);
        setCurrentProcessName('Cálculo de Liquidaciones Pendientes');
        setProgressModalOpen(true);
        toast.success(data.message);
      } else {
        toast.error('Error iniciando cálculo de liquidaciones');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error calculando liquidaciones');
    }
  });

  // Mutación para calcular decomisiones (asíncrona con modal)
  const decomisionMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post('/api/liquidaciones/decomisiones/calcular/start?limite=0');
      return res.data;
    },
    onSuccess: (data) => {
      if (data.success && data.job_id) {
        setCurrentJobId(data.job_id);
        setCurrentProcessName('Cálculo de Decomisiones');
        setProgressModalOpen(true);
        toast.success(data.message);
      } else {
        toast.error('Error iniciando cálculo de decomisiones');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error calculando decomisiones');
    }
  });

  // Mutaciones para acciones batch (asíncronas con modal de progreso)
  const liquidarBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/liquidar/start', { registros });
      return res.data;
    },
    onSuccess: (data) => {
      if (data.success && data.job_id) {
        setCurrentJobId(data.job_id);
        setCurrentProcessName('Liquidación de Registros Seleccionados');
        setProgressModalOpen(true);
        toast.success(data.message);
        setSelectedRows(new Set());
      } else {
        toast.error('Error iniciando liquidación batch');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error en liquidación múltiple');
    }
  });

  const decomisionarBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/decomisionar/start', { registros });
      return res.data;
    },
    onSuccess: (data) => {
      if (data.success && data.job_id) {
        setCurrentJobId(data.job_id);
        setCurrentProcessName('Decomisión de Registros Seleccionados');
        setProgressModalOpen(true);
        toast.success(data.message);
        setSelectedRows(new Set());
      } else {
        toast.error('Error iniciando decomisión batch');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error en decomisión múltiple');
    }
  });

  const bloquearLiquidacionBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/bloquear-liquidacion', { registros });
      return res.data;
    },
    onSuccess: async (data) => {
      const stats = data.data || {};
      if (stats.fallidos > 0) {
        toast.warning(
          `${stats.exitosos} bloqueadas, ${stats.fallidos} fallidas. ${stats.errores?.[0] || ''}`,
          { duration: 5000 }
        );
      } else {
        toast.success(data.message);
      }
      setSelectedRows(new Set());
      await queryClient.refetchQueries(['liquidaciones']);
      await queryClient.refetchQueries(['liquidaciones-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error bloqueando liquidaciones');
    }
  });

  const bloquearDecomisionBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/bloquear-decomision', { registros });
      return res.data;
    },
    onSuccess: async (data) => {
      const stats = data.data || {};
      if (stats.fallidos > 0) {
        toast.warning(
          `${stats.exitosos} bloqueadas, ${stats.fallidos} fallidas. ${stats.errores?.[0] || ''}`,
          { duration: 5000 }
        );
      } else {
        toast.success(data.message);
      }
      setSelectedRows(new Set());
      await queryClient.refetchQueries(['liquidaciones']);
      await queryClient.refetchQueries(['liquidaciones-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error bloqueando decomisiones');
    }
  });

  const desbloquearLiquidacionBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/desbloquear-liquidacion', { registros });
      return res.data;
    },
    onSuccess: async (data) => {
      const stats = data.data || {};
      toast.success(data.message);
      setSelectedRows(new Set());
      await queryClient.refetchQueries(['liquidaciones']);
      await queryClient.refetchQueries(['liquidaciones-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error desbloqueando liquidaciones');
    }
  });

  const desbloquearDecomisionBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/desbloquear-decomision', { registros });
      return res.data;
    },
    onSuccess: async (data) => {
      const stats = data.data || {};
      toast.success(data.message);
      setSelectedRows(new Set());
      await queryClient.refetchQueries(['liquidaciones']);
      await queryClient.refetchQueries(['liquidaciones-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error desbloqueando decomisiones');
    }
  });

  const resetearBatchMutation = useMutation({
    mutationFn: async (registros) => {
      const res = await axios.post('/api/liquidaciones/batch/resetear', { registros });
      return res.data;
    },
    onSuccess: async (data) => {
      const stats = data.data || {};
      toast.success(data.message);
      setSelectedRows(new Set());
      await queryClient.refetchQueries(['liquidaciones']);
      await queryClient.refetchQueries(['liquidaciones-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error reseteando liquidaciones y decomisiones');
    }
  });

  // Handler para cuando se cierra el modal
  const handleModalClose = () => {
    setProgressModalOpen(false);
    setCurrentJobId(null);
    setCurrentProcessName('');
    
    // Refrescar datos inmediatamente
    queryClient.refetchQueries(['liquidaciones']);
    queryClient.refetchQueries(['liquidaciones-stats']);
    // El job recién terminado debe aparecer ya en el desplegable "Procesada en Job"
    queryClient.refetchQueries(['jobs-filtro-liquidaciones']);
  };

  // Handler para ver resumen de liquidación
  const handleVerResumen = async (cups, fechaInicio) => {
    try {
      setLoadingResumen(true);
      const fechaFormatted = fechaInicio.split('T')[0]; // Asegurar formato YYYY-MM-DD
      const res = await axios.get(`/api/liquidaciones/resumen/${cups}/${fechaFormatted}/`);
      
      if (res.data.success) {
        setResumenData(res.data.data);
        setResumenModalOpen(true);
      } else {
        toast.error('No se pudo obtener el resumen');
      }
    } catch (error) {
      console.error('Error obteniendo resumen:', error);
      toast.error(error.response?.data?.error || 'Error al obtener el resumen de liquidación');
    } finally {
      setLoadingResumen(false);
    }
  };

  // Handler para cerrar modal de resumen
  const handleCloseResumen = () => {
    setResumenModalOpen(false);
    setResumenData(null);
  };

  // Handlers para selección múltiple
  const handleSelectRow = (rowId) => {
    setTodosFiltradosSeleccionados(false);
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!liquidacionesData?.data) return;
    
    // Cualquier fila es seleccionable (acciones no aplicables se rechazan por fila en el backend)
    const allIds = liquidacionesData.data.map(row => `${row.cups}-${row.fecha_inicio_producto}`);
    
    // Si todas están seleccionadas, deseleccionar todas
    const allSelected = allIds.every(id => selectedRows.has(id));
    
    if (allSelected && selectedRows.size > 0) {
      setSelectedRows(new Set());
      setTodosFiltradosSeleccionados(false);
    } else {
      setSelectedRows(new Set(allIds));
      setTodosFiltradosSeleccionados(false);
    }
  };

  // Selecciona el 100% de los registros que cumplen el filtro actual, no solo los
  // de la página visible (pide los ids al backend sin paginar)
  const handleSeleccionarTodosFiltrados = async () => {
    setLoadingSeleccionarTodos(true);
    try {
      const params = buildFiltrosParams();
      const res = await axios.get(`/api/liquidaciones/ids/?${params}`);
      const { ids, total, truncado } = res.data;
      setSelectedRows(new Set(ids.map(({ cups, fecha_inicio_producto }) => `${cups}-${fecha_inicio_producto}`)));
      setTodosFiltradosSeleccionados(true);
      if (truncado) {
        toast.warning(`Se seleccionaron los primeros ${ids.length.toLocaleString()} de ${total.toLocaleString()} registros (límite de selección alcanzado)`);
      } else {
        toast.success(`${ids.length.toLocaleString()} registros seleccionados`);
      }
    } catch (error) {
      console.error('Error seleccionando todos los registros filtrados:', error);
      toast.error(error.response?.data?.error || 'Error al seleccionar todos los registros');
    } finally {
      setLoadingSeleccionarTodos(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedRows(new Set());
    setTodosFiltradosSeleccionados(false);
  };

  const handleExportar = async () => {
    setIsExporting(true);
    try {
      const params = buildFiltrosParams();
      const response = await axios.get(`/api/liquidaciones/export/?${params}`, {
        responseType: 'blob'
      });

      const contentDisposition = response.headers['content-disposition'] || '';
      const fileNameMatch = contentDisposition.match(/filename=([^;]+)/i);
      const fileName = (fileNameMatch?.[1] || `liquidaciones_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`).replace(/"/g, '');

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const total = Number(response.headers['x-export-total'] || 0);
      const truncado = response.headers['x-export-truncated'] === 'true';
      if (truncado) {
        toast.warning(`Exportación generada con ${total.toLocaleString()} registros (se alcanzó el límite de seguridad)`);
      } else {
        toast.success(total > 0 ? `Exportación completada: ${total.toLocaleString()} registros` : 'Exportación completada');
      }
    } catch (error) {
      let message = 'Error exportando liquidaciones';
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const parsed = JSON.parse(errorText);
          if (parsed?.error) {
            message = parsed.error;
          }
        } catch {
          // Ignorar parsing fallido y conservar mensaje por defecto
        }
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }
      toast.error(message);
    } finally {
      setIsExporting(false);
    }
  };

  // Handlers para acciones batch
  const handleLiquidarBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Liquidar ${selectedRows.size} registros seleccionados?`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      liquidarBatchMutation.mutate(registros);
    }
  };

  const handleDecomisionarBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Decomisionar pendientes en ${selectedRows.size} registros seleccionados?`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      decomisionarBatchMutation.mutate(registros);
    }
  };

  const handleBloquearLiquidacionBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Bloquear liquidación de ${selectedRows.size} registros seleccionados?`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      bloquearLiquidacionBatchMutation.mutate(registros);
    }
  };

  const handleBloquearDecomisionBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Bloquear decomisión de ${selectedRows.size} registros seleccionados?`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      bloquearDecomisionBatchMutation.mutate(registros);
    }
  };

  const handleDesbloquearLiquidacionBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Desbloquear liquidación de ${selectedRows.size} registros seleccionados?`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      desbloquearLiquidacionBatchMutation.mutate(registros);
    }
  };

  const handleDesbloquearDecomisionBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Desbloquear decomisión de ${selectedRows.size} registros seleccionados?`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      desbloquearDecomisionBatchMutation.mutate(registros);
    }
  };

  const handleResetearBatch = () => {
    if (selectedRows.size === 0) return;
    
    if (window.confirm(`¿Resetear liquidación y decomisión de ${selectedRows.size} registros? Esto eliminará todos los cálculos y los dejará en estado pendiente.`)) {
      const registros = Array.from(selectedRows).map(id => {
        const [cups, ...fechaParts] = id.split('-');
        return {
          cups,
          fecha_inicio_producto: fechaParts.join('-')
        };
      });
      resetearBatchMutation.mutate(registros);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const clearFilters = () => {
    setFilters({
      estado_historificacion: '',
      canal: '',
      tarifa_atr: '',
      id_tarifa_precios: '',
      esquema_tarifa: '',
      liquidacion_estado: '',
      decomision_estado: '',
      tipo_persona: '',
      estado: '',
      subestado: '',
      fecha_inicio_desde: '',
      fecha_inicio_hasta: '',
      fecha_fin_desde: '',
      fecha_fin_hasta: '',
      liquidacion_fecha_calculo_desde: '',
      liquidacion_fecha_calculo_hasta: '',
      decomision_fecha_calculo_desde: '',
      decomision_fecha_calculo_hasta: '',
      porcentaje_extra_broker_operador: '=',
      porcentaje_extra_broker_valor: '',
      job_id: ''
    });
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const hasActiveFilters = searchInput || Object.entries(filters).some(([k, v]) => {
    // El operador por sí solo (sin valor) no cuenta como filtro activo
    if (k === 'porcentaje_extra_broker_operador') return false;
    return v !== '';
  });

  return (
    <div className="flex flex-col h-full w-full">
      {/* Barra de acciones batch (aparece cuando hay selección) */}
      {selectedRows.size > 0 && (
        <div className="flex items-center justify-between gap-4 px-3 py-2 bg-blue-50 border-b border-blue-300">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-blue-800">
              {selectedRows.size} seleccionado{selectedRows.size > 1 ? 's' : ''}
              {todosFiltradosSeleccionados && ' (todos los resultados del filtro)'}
            </span>
            <div className="h-4 w-px bg-blue-300"></div>
            <button
              onClick={handleLiquidarBatch}
              disabled={liquidarBatchMutation.isLoading}
              className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
            >
              <Calculator className="h-3 w-3" />
              Liquidar
            </button>
            <button
              onClick={handleDecomisionarBatch}
              disabled={decomisionarBatchMutation.isLoading}
              className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
              >
                <DollarSign className="h-3 w-3" />
                Decomisionar Pendientes
              </button>
              <button
                onClick={handleBloquearLiquidacionBatch}
                disabled={bloquearLiquidacionBatchMutation.isLoading}
                className="flex items-center gap-1 px-2 py-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
              >
                <Lock className="h-3 w-3" />
                Bloq. Liq.
              </button>
              <button
                onClick={handleBloquearDecomisionBatch}
                disabled={bloquearDecomisionBatchMutation.isLoading}
                className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
              >
                <Lock className="h-3 w-3" />
                Bloq. Dec.
              </button>
              <button
                onClick={handleDesbloquearLiquidacionBatch}
                disabled={desbloquearLiquidacionBatchMutation.isLoading}
                className="flex items-center gap-1 px-2 py-1 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
              >
                <Unlock className="h-3 w-3" />
                Desb. Liq.
              </button>
              <button
                onClick={handleDesbloquearDecomisionBatch}
                disabled={desbloquearDecomisionBatchMutation.isLoading}
                className="flex items-center gap-1 px-2 py-1 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
              >
                <Unlock className="h-3 w-3" />
                Desb. Dec.
              </button>
              <div className="h-4 w-px bg-blue-300"></div>
              <button
                onClick={handleResetearBatch}
                disabled={resetearBatchMutation.isLoading}
                className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Reset Liq/Dec
              </button>
              <div className="h-4 w-px bg-blue-300"></div>
              <button
                onClick={handleClearSelection}
                className="flex items-center gap-1 px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs font-medium transition-colors"
              >
                <X className="h-3 w-3" />
                Limpiar
              </button>
          </div>
        </div>
      )}

      {/* Banner para extender la selección a TODOS los resultados del filtro, no solo la página visible */}
      {selectedRows.size > 0 && !todosFiltradosSeleccionados && liquidacionesData &&
        selectedRows.size === liquidacionesData.data.length &&
        liquidacionesData.total > liquidacionesData.data.length && (
        <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-blue-50 border-b border-blue-200 text-xs text-blue-800">
          <span>Los {selectedRows.size} registros de esta página están seleccionados.</span>
          <button
            onClick={handleSeleccionarTodosFiltrados}
            disabled={loadingSeleccionarTodos}
            className="font-semibold underline hover:text-blue-900 disabled:opacity-50"
          >
            {loadingSeleccionarTodos
              ? 'Seleccionando...'
              : `Seleccionar los ${liquidacionesData.total.toLocaleString()} registros que coinciden con el filtro`}
          </button>
        </div>
      )}

      {/* Filtros - panel colapsable */}
      <div className="bg-white border-b border-gray-200">
        <button
          type="button"
          onClick={() => setFiltersOpen((open) => !open)}
          className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          {filtersOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Filtros
          {hasActiveFilters && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
              activos
            </span>
          )}
        </button>

        {filtersOpen && (
        <div
          className="grid gap-2 px-3 pb-2.5 items-stretch"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
        >
        {/* Búsqueda */}
        <div className="relative col-span-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar CUPS o Cliente..."
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            className="w-full h-full pl-8 pr-2 py-1.5 border border-gray-300 rounded text-xs"
          />
        </div>

        <select
          value={filters.estado_historificacion}
          onChange={(e) => { setFilters({ ...filters, estado_historificacion: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        >
          <option value="">Estado Histor.</option>
          <option value="PENDIENTE LIQUIDAR">Pend. Liquidar</option>
          <option value="NO LIQUIDABLE">No Liquidable</option>
          <option value="ACTIVACION FUTURA">Activación Futura</option>
          <option value="LIQUIDADO">Liquidado</option>
          <option value="PENDIENTE DECOMISIONAR">Pend. Decomisionar</option>
          <option value="FINALIZADO">Finalizado</option>
          <option value="ERROR LIQUIDACIÓN">Error Liquidación</option>
          <option value="ERROR DECOMISIÓN">Error Decomisión</option>
          <option value="LIQ. BLOQUEADA">Liq. Bloqueada</option>
          <option value="DECOM. BLOQUEADA">Decom. Bloqueada</option>
        </select>
        <input
          type="text"
          placeholder="Canal"
          value={filters.canal}
          onChange={(e) => { setFilters({ ...filters, canal: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
        <input
          type="text"
          placeholder="Tarifa ATR"
          value={filters.tarifa_atr}
          onChange={(e) => { setFilters({ ...filters, tarifa_atr: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
        <select
          value={filters.esquema_tarifa}
          onChange={(e) => { setFilters({ ...filters, esquema_tarifa: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
          title="Filtrar por esquema de liquidación de la tarifa"
        >
          <option value="">Esquema Tarifa</option>
          <option value="EASY">Easy (Todos)</option>
          {esquemasTarifaData?.data
            ?.filter((item) => !EASY_LABELS[item.esquema])
            .map((item) => (
              <option key={item.esquema} value={item.esquema}>
                {getEsquemaTarifaLabel(item.esquema, esquemasVolumenCups)}
              </option>
            ))}
          {esquemasTarifaData?.data
            ?.filter((item) => EASY_LABELS[item.esquema])
            .map((item) => (
              <option key={item.esquema} value={item.esquema}>
                {getEsquemaTarifaLabel(item.esquema, esquemasVolumenCups)}
              </option>
            ))}
        </select>
        <div className="col-span-2 flex items-center gap-1 px-2 py-1 border border-gray-300 rounded bg-white">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">% Extra margen</span>
          <select
            value={filters.porcentaje_extra_broker_operador}
            onChange={(e) => { setFilters({ ...filters, porcentaje_extra_broker_operador: e.target.value }); setPage(1); }}
            className="w-14 border-0 p-0 focus:ring-0 focus:outline-none bg-transparent text-xs"
            title="Operador de comparación para % Extra margen"
          >
            <option value="=">=</option>
            <option value=">">&gt;</option>
            <option value=">=">&gt;=</option>
            <option value="<">&lt;</option>
            <option value="<=">&lt;=</option>
          </select>
          <input
            type="number"
            step="0.01"
            placeholder="Valor"
            value={filters.porcentaje_extra_broker_valor}
            onChange={(e) => { setFilters({ ...filters, porcentaje_extra_broker_valor: e.target.value }); setPage(1); }}
            className="flex-1 min-w-0 border-0 p-0 focus:ring-0 focus:outline-none bg-transparent text-xs"
            title="Filtrar por % Extra broker de la tarifa"
          />
        </div>
        <select
          value={filters.liquidacion_estado}
          onChange={(e) => { setFilters({ ...filters, liquidacion_estado: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        >
          <option value="">Estado Liq.</option>
          <option value="pendiente">Pendiente</option>
          <option value="calculada">Calculada</option>
          <option value="bloqueada">Bloqueada</option>
          <option value="no_liquidable">No Liquidable</option>
          <option value="error">Error</option>
        </select>
        <select
          value={filters.decomision_estado}
          onChange={(e) => { setFilters({ ...filters, decomision_estado: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        >
          <option value="">Estado Dec.</option>
          <option value="pendiente">Pendiente</option>
          <option value="calculada">Calculada</option>
          <option value="bloqueada">Bloqueada</option>
          <option value="error">Error</option>
        </select>
        <select
          value={filters.job_id}
          onChange={(e) => { setFilters({ ...filters, job_id: e.target.value }); setPage(1); }}
          className="w-full col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs"
          title="Mostrar solo las filas procesadas por un job de liquidación o decomisión"
        >
          <option value="">Procesada en Job</option>
          {jobsFiltroConActual.map((job) => (
            <option key={job.id} value={job.id}>
              {JOB_TIPO_PROCESO_LABELS[job.tipo_proceso] || job.tipo_proceso}
              {' · '}
              {job.fecha_inicio ? new Date(job.fecha_inicio).toLocaleString() : '-'}
              {' · '}
              {job.id.slice(0, 8)}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="ID Tarifa"
          value={filters.id_tarifa_precios}
          onChange={(e) => { setFilters({ ...filters, id_tarifa_precios: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
        <input
          type="text"
          placeholder="Tipo Pers."
          value={filters.tipo_persona}
          onChange={(e) => { setFilters({ ...filters, tipo_persona: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
        <input
          type="text"
          placeholder="Estado Cont."
          value={filters.estado}
          onChange={(e) => { setFilters({ ...filters, estado: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
        <input
          type="text"
          placeholder="Subestado"
          value={filters.subestado}
          onChange={(e) => { setFilters({ ...filters, subestado: e.target.value }); setPage(1); }}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
        <DateRangeFilter
          label="F.Inicio"
          desde={filters.fecha_inicio_desde}
          hasta={filters.fecha_inicio_hasta}
          onDesdeChange={(v) => { setFilters({ ...filters, fecha_inicio_desde: v }); setPage(1); }}
          onHastaChange={(v) => { setFilters({ ...filters, fecha_inicio_hasta: v }); setPage(1); }}
          titleDesde="Fecha inicio producto desde"
          titleHasta="Fecha inicio producto hasta"
          className="col-span-2"
        />
        <DateRangeFilter
          label="F.Fin"
          desde={filters.fecha_fin_desde}
          hasta={filters.fecha_fin_hasta}
          onDesdeChange={(v) => { setFilters({ ...filters, fecha_fin_desde: v }); setPage(1); }}
          onHastaChange={(v) => { setFilters({ ...filters, fecha_fin_hasta: v }); setPage(1); }}
          titleDesde="Fecha fin producto desde"
          titleHasta="Fecha fin producto hasta"
          className="col-span-2"
        />
        <DateRangeFilter
          label="F.Cálc. Liq."
          desde={filters.liquidacion_fecha_calculo_desde}
          hasta={filters.liquidacion_fecha_calculo_hasta}
          onDesdeChange={(v) => { setFilters({ ...filters, liquidacion_fecha_calculo_desde: v }); setPage(1); }}
          onHastaChange={(v) => { setFilters({ ...filters, liquidacion_fecha_calculo_hasta: v }); setPage(1); }}
          titleDesde="Fecha de cálculo de la liquidación desde"
          titleHasta="Fecha de cálculo de la liquidación hasta"
          className="col-span-2"
        />
        <DateRangeFilter
          label="F.Cálc. Dec."
          desde={filters.decomision_fecha_calculo_desde}
          hasta={filters.decomision_fecha_calculo_hasta}
          onDesdeChange={(v) => { setFilters({ ...filters, decomision_fecha_calculo_desde: v }); setPage(1); }}
          onHastaChange={(v) => { setFilters({ ...filters, decomision_fecha_calculo_hasta: v }); setPage(1); }}
          titleDesde="Fecha de cálculo de la decomisión desde"
          titleHasta="Fecha de cálculo de la decomisión hasta"
          className="col-span-2"
        />
        </div>
        )}
      </div>

      {/* Botones de acción: se insertan a la derecha del renglón de submenús (App.jsx) */}
      {navActionsSlot && createPortal(
        <div className="flex items-center gap-1.5 flex-wrap p-1.5 rounded-md border border-gray-200 bg-gray-50">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 px-2 py-1.5 border border-gray-300 bg-white rounded whitespace-nowrap"
            >
              <X className="h-3 w-3" />
              Limpiar
            </button>
          )}

          <button
            onClick={() => {
              refetch();
              toast.success('Listado refrescado');
            }}
            disabled={isLoading}
            className="text-xs px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
            title="Refrescar listado con los filtros actuales"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refrescar
          </button>

          <div className="h-6 w-px bg-gray-300 mx-1"></div>

          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            title={`Sincroniza registros con fecha inicio producto de hasta ${mesesSync} meses de antigüedad`}
            className="text-xs px-3 py-1.5 bg-primary hover:bg-primary-dark text-white rounded flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            {syncMutation.isPending ? 'Sync...' : 'Sincronizar'}
          </button>
          <input
            type="number"
            min={1}
            value={mesesSync}
            onChange={handleMesesSyncChange}
            onClick={(e) => e.stopPropagation()}
            title="Meses de antigüedad de fecha inicio producto a sincronizar"
            className="w-14 text-xs px-1.5 py-1.5 border border-gray-300 rounded text-center"
          />
          <button
            onClick={() => syncMutation.mutate(MESES_SYNC_ULTIMO_ANIO)}
            disabled={syncMutation.isPending}
            title="Sincroniza sólo registros con fecha inicio producto dentro del último año"
            className="text-xs px-3 py-1.5 bg-primary hover:bg-primary-dark text-white rounded flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
          >
            <Clock className={`h-3.5 w-3.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            {syncMutation.isPending ? 'Sync...' : 'Sync. último año'}
          </button>
          <button
            onClick={() => calcularMutation.mutate()}
            disabled={calcularMutation.isPending}
            className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
          >
            <Calculator className={`h-3.5 w-3.5 ${calcularMutation.isPending ? 'animate-spin' : ''}`} />
            {calcularMutation.isPending ? 'Calc...' : 'Liquidar Pendientes'}
          </button>
          <button
            onClick={() => decomisionMutation.mutate()}
            disabled={decomisionMutation.isPending}
            className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
          >
            <DollarSign className={`h-3.5 w-3.5 ${decomisionMutation.isPending ? 'animate-spin' : ''}`} />
            {decomisionMutation.isPending ? 'Calc...' : 'Decomisionar Pendientes'}
          </button>
          <button
            onClick={handleExportar}
            disabled={isExporting}
            className="text-xs px-3 py-1.5 border border-gray-300 bg-white rounded flex items-center gap-1.5 whitespace-nowrap hover:bg-gray-50 disabled:opacity-50"
            title="Exportar CSV con los filtros actuales y todas las columnas"
          >
            <Download className="h-3.5 w-3.5" />
            {isExporting ? 'Exportando...' : 'Exportar'}
          </button>
        </div>,
        navActionsSlot
      )}

      {/* Tabla de liquidaciones */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white border-t border-gray-200">
        {/* Control de secciones compacto */}
        <div className="px-1 py-1.5 border-b border-gray-200 bg-gray-50 flex items-center gap-1.5 overflow-x-auto shrink-0">
          <span className="text-xs text-gray-600 mr-2">Columnas:</span>

          <button
            onClick={() => toggleSection('favoritas')}
            className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
              expandedSections.favoritas ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            title="Mostrar/ocultar la sección de columnas favoritas"
          >
            {expandedSections.favoritas ? '−' : '+'} Favoritas{favoriteKeysOrdered.length > 0 ? ` (${favoriteKeysOrdered.length})` : ''}
          </button>

          <div className="relative">
            <button
              ref={favoritosButtonRef}
              onClick={() => {
                if (!favoritosPanelOpen) {
                  const rect = favoritosButtonRef.current?.getBoundingClientRect();
                  if (rect) setFavoritosPanelPos({ top: rect.bottom + 4, left: rect.left });
                }
                setFavoritosPanelOpen(o => !o);
              }}
              className="text-xs px-2 py-0.5 rounded whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200"
              title="Elegir qué columnas marcar como favoritas"
            >
              Elegir columnas
            </button>
            {favoritosPanelOpen && favoritosPanelPos && createPortal(
              <>
                {/* Overlay para cerrar el panel al hacer click fuera */}
                <div className="fixed inset-0 z-40" onClick={() => setFavoritosPanelOpen(false)} />
                <div
                  className="fixed z-50 w-[420px] max-h-96 overflow-auto bg-white border border-gray-300 rounded shadow-lg p-2"
                  style={{ top: favoritosPanelPos.top, left: favoritosPanelPos.left }}
                >
                  <div className="flex items-center justify-between mb-2 sticky -top-2 bg-white pb-1">
                    <span className="text-xs font-semibold text-gray-700">Seleccionar columnas favoritas</span>
                    <button onClick={() => setFavoritosPanelOpen(false)} className="text-gray-400 hover:text-gray-700">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {Object.entries(favoritoColumnsBySection).map(([sectionKey, cols]) => (
                    <div key={sectionKey} className="mb-2">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{SECTION_LABELS[sectionKey] || sectionKey}</div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                        {cols.map(col => (
                          <label key={col.key} className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={favoriteColumns.has(col.key)}
                              onChange={() => toggleFavoritoColumn(col.key)}
                              className="h-3 w-3 rounded border-gray-300 text-gray-600 focus:ring-gray-400"
                            />
                            {col.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>,
              document.body
            )}
          </div>

          <div className="h-4 w-px bg-gray-300 mx-0.5"></div>

          {[
            { key: 'identificacion', label: 'Identif.' },
            { key: 'contrato', label: 'Contrato' },
            { key: 'tecnico', label: 'Técnico' },
            { key: 'tarifa', label: 'Tarifa' },
            { key: 'potencias', label: 'Potencias' },
            { key: 'margenes_energia', label: 'Marg.Energ.' },
            { key: 'margenes_potencia', label: 'Marg.Pot.' },
            { key: 'otras_condiciones', label: 'Otras Cond.' },
            { key: 'liquidacion_datos', label: 'Datos Liq.' },
            { key: 'liquidacion', label: 'Liquidación' },
            { key: 'decomision', label: 'Decomisión' }
          ].map(section => (
            <button
              key={section.key}
              onClick={() => toggleSection(section.key)}
              className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
                expandedSections[section.key] ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {expandedSections[section.key] ? '−' : '+'} {section.label}
            </button>
          ))}
          {liquidacionesData && (
            <span className="ml-auto text-xs text-gray-600">
              {liquidacionesData.data.length} de {liquidacionesData.total.toLocaleString()} registros
            </span>
          )}
        </div>

        {/* Contenido de tabla */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <span className="ml-2 text-gray-600">Cargando liquidaciones...</span>
          </div>
        ) : liquidacionesData && liquidacionesData.data.length > 0 ? (
          <div className="flex-1 overflow-auto w-full">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  {/* Checkbox seleccionar todos */}
                  <th className="px-2 py-1 text-center text-xs font-semibold text-white bg-gradient-to-r from-gray-600 to-gray-500 uppercase whitespace-nowrap sticky left-0 z-20">
                    <input
                      type="checkbox"
                      checked={liquidacionesData?.data && (() => {
                        const allIds = liquidacionesData.data.map(row => `${row.cups}-${row.fecha_inicio_producto}`);
                        return allIds.length > 0 && allIds.every(id => selectedRows.has(id));
                      })()}
                      onChange={handleSelectAll}
                      title="Seleccionar todos los registros"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  
                  {/* Estado Historificación - Sin agrupación, siempre visible */}
                  <th className="px-2 py-1 text-left text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-500 uppercase whitespace-nowrap sticky left-10 z-20">Estado</th>
                  
                  {/* Acciones - Siempre visible */}
                  <th className="px-2 py-1 text-center text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 uppercase whitespace-nowrap">Acciones</th>

                  {/* Favoritas - columnas fijadas por el usuario, siempre a la izquierda */}
                  {expandedSections.favoritas && favoriteKeysOrdered.map(key => (
                    <th key={`fav-th-${key}`} className="px-1 py-1 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap bg-gray-100 border-l-2 border-gray-300">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleFavoritoColumn(key)}
                          title="Quitar de Favoritas"
                          aria-label="Quitar de Favoritas"
                          className="text-gray-400 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <span>{favoritoColumnsMap[key]?.label}</span>
                      </div>
                    </th>
                  ))}
                  
                  {/* Identificación */}
                  {expandedSections.identificacion && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-blue-400">ID</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CUPS</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Identificador</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Cliente</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tipo Pers.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Canal</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">PS ID</th>
                    </>
                  )}
                  
                  {/* Contrato */}
                  {expandedSections.contrato && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-green-400">Código</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tarifa ATR</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Nombre Tarifa</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Estado</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Subestado</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tipo Cont. ATR</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Inicio</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Fin</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Ini.Prod</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Fin Prod</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Firma</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Permanencia</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Perm. Días</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Perm.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Fin Est.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Ini Info</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Updated At</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Created At</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Updated BOE</th>
                    </>
                  )}
                  
                  {/* Técnico */}
                  {expandedSections.tecnico && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-amber-400">Cód. Distrib.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Distribuidora</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Provincia</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Municipio</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CNAE</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE Anual Est. (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tipo Autocons.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Act.Autocons.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Batería Virtual</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">ID Batería</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Carga Batería</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tipo Mandato</th>
                    </>
                  )}
                  
                  {/* Tarifa */}
                  {expandedSections.tarifa && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-purple-400">ID Tarifa</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Motivo Precio</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tipo Tarifa</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Descripción Tarifa</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Alias Externo</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fee Indexado</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Esquema Tarifa</th>
                    </>
                  )}
                  
                  {/* Potencias */}
                  {expandedSections.potencias && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-teal-400">P1 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P2 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P3 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P4 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P5 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P6 (kW)</th>
                    </>
                  )}
                  
                  {/* Márgenes Energía */}
                  {expandedSections.margenes_energia && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-orange-400">E1 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E2 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E3 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E4 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E5 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E6 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P1 €/kW</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P2 €/kW</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P3 €/kW</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P4 €/kW</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P5 €/kW</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P6 €/kW</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E1 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E2 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E3 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E4 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E5 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">E6 Final</th>
                    </>
                  )}
                  
                  {/* Márgenes Potencia */}
                  {expandedSections.margenes_potencia && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-pink-400">P1 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P2 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P3 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P4 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P5 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P6 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P1 €/kW/año</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P2 €/kW/año</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P3 €/kW/año</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P4 €/kW/año</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P5 €/kW/año</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Ext.P6 €/kW/año</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P1 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P2 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P3 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P4 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P5 Final</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">P6 Final</th>
                    </>
                  )}
                  
                  {/* Otras Condiciones */}
                  {expandedSections.otras_condiciones && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-indigo-400">A1 Base</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Margen Exc</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Porc. Exc</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Años</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Meses</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Límite Serv.Ajustes</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Observaciones</th>
                    </>
                  )}
                  
                  {/* Datos Liquidación */}
                  {expandedSections.liquidacion_datos && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-cyan-400">Tipo Liq.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Esquema Liquidacion</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Origen Consumo</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Porc.Extra Broker</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Com. €/MWh</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Com. €/CUPS</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE P1 (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE P2 (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE P3 (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE P4 (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE P5 (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">CAE P6 (kWh)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pot.P1 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pot.P2 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pot.P3 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pot.P4 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pot.P5 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pot.P6 (kW)</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Ini.Prod.Liq</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Fin.Prod.Liq</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Días Incl.</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Consumo Anual (kWh)</th>
                    </>
                  )}
                  
                  {/* Liquidación */}
                  {expandedSections.liquidacion && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-green-500">Estado Liq</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Error Liq</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Liquidación €</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Extramargen €</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Cálculo</th>
                    </>
                  )}
                  
                  {/* Decomisión */}
                  {expandedSections.decomision && (
                    <>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-l-4 border-purple-500">Estado Dec</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Error Dec</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Decomisión €</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Días Dec</th>
                      <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fec.Cálculo</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {liquidacionesData.data.map((row, idx) => {
                  const rowId = `${row.cups}-${row.fecha_inicio_producto}`;
                  const isSelected = selectedRows.has(rowId);
                  
                  return (
                    <tr key={rowId} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      {/* Checkbox de selección */}
                      <td className="px-2 py-1 text-center whitespace-nowrap sticky left-0 z-10 bg-white border-r border-gray-200">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowId)}
                          title="Seleccionar para procesar/resetear"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      
                      {/* Estado Historificación - Sin agrupación, siempre visible */}
                      <td className="px-2 py-1 text-xs font-semibold whitespace-nowrap sticky left-10 z-10 bg-white border-r border-gray-200">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          row.estado_historificacion === 'FINALIZADO' ? 'bg-green-100 text-green-800' :
                          row.estado_historificacion === 'LIQUIDADO' ? 'bg-blue-100 text-blue-800' :
                          row.estado_historificacion === 'ACTIVACION FUTURA' ? 'bg-indigo-100 text-indigo-800' :
                          row.estado_historificacion === 'PENDIENTE LIQUIDAR' ? 'bg-yellow-100 text-yellow-800' :
                          row.estado_historificacion === 'NO LIQUIDABLE' ? 'bg-rose-100 text-rose-800' :
                          row.estado_historificacion === 'PENDIENTE DECOMISIONAR' ? 'bg-orange-100 text-orange-800' :
                          row.estado_historificacion === 'ERROR LIQUIDACIÓN' ? 'bg-red-100 text-red-800' :
                          row.estado_historificacion === 'ERROR DECOMISIÓN' ? 'bg-red-100 text-red-800' :
                          row.estado_historificacion === 'LIQ. BLOQUEADA' ? 'bg-purple-100 text-purple-800' :
                          row.estado_historificacion === 'DECOM. BLOQUEADA' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {row.estado_historificacion || '-'}
                        </span>
                      </td>
                    
                    {/* Acciones - Siempre visible */}
                    <td className="px-2 py-1 text-center whitespace-nowrap bg-white">
                      {(row.liquidacion_estado === 'calculada' || row.decomision_estado === 'calculada') && (
                        <button
                          onClick={() => handleVerResumen(row.cups, row.fecha_inicio_producto)}
                          disabled={loadingResumen}
                          className="inline-flex items-center justify-center p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                          title={
                            row.liquidacion_estado === 'calculada' && row.decomision_estado === 'calculada'
                              ? 'Ver resumen de liquidación y decomisión'
                              : row.liquidacion_estado === 'calculada'
                              ? 'Ver resumen de liquidación'
                              : 'Ver resumen de decomisión'
                          }
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      )}
                    </td>

                    {/* Favoritas - columnas fijadas por el usuario, siempre a la izquierda */}
                    {expandedSections.favoritas && favoriteKeysOrdered.map(key => (
                      <td
                        key={`fav-td-${key}-${rowId}`}
                        className={`px-1 py-0.5 text-xs bg-gray-50 border-l-2 border-gray-200 ${favoritoColumnsMap[key]?.align === 'right' ? 'text-right' : ''}`}
                      >
                        {favoritoColumnsMap[key]?.render(row)}
                      </td>
                    ))}
                    
                    {/* Identificación */}
                    {expandedSections.identificacion && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 border-l-4 border-blue-400">{row.id || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900 font-mono whitespace-nowrap">{row.cups || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.identificador || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900 max-w-xs truncate">{row.nombre_completo || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.tipo_persona || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900">
                          {row.canal ? (
                            <span className="inline-flex items-center gap-1 max-w-full">
                              <span className="truncate" title={row.canal}>{row.canal}</span>
                              <button
                                type="button"
                                title={`Desactivar canal ${row.canal}`}
                                aria-label={`Desactivar canal ${row.canal}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setCanalParaDesactivar(row.canal);
                                }}
                                className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                              >
                                <Ban className="h-3 w-3" />
                              </button>
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.ps_id || '-'}</td>
                      </>
                    )}
                    
                    {/* Contrato */}
                    {expandedSections.contrato && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 border-l-4 border-green-400">{row.codigo || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900 font-medium">{row.tarifa_atr || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 max-w-xs truncate">{row.nombre || '-'}</td>
                        <td className="px-1 py-0.5 text-xs">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            row.estado === 'Activo' ? 'bg-green-100 text-green-800' :
                            row.estado === 'Anulado' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {row.estado || '-'}
                          </span>
                        </td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.subestado || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.tipo_contrato_atr || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_inicio ? new Date(row.fecha_inicio).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_fin ? new Date(row.fecha_fin).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900">{row.fecha_inicio_producto ? new Date(row.fecha_inicio_producto).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900">{row.fecha_fin_producto ? new Date(row.fecha_fin_producto).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_firma ? new Date(row.fecha_firma).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.permanencia || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.permanencia_duracion_dias || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_permanencia ? new Date(row.fecha_permanencia).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_fin_estimada ? new Date(row.fecha_fin_estimada).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_inicio_info ? new Date(row.fecha_inicio_info).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.updated_at ? new Date(row.updated_at).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.created_at ? new Date(row.created_at).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.updated_at_boe ? new Date(row.updated_at_boe).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                      </>
                    )}
                    
                    {/* Técnico */}
                    {expandedSections.tecnico && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 border-l-4 border-amber-400">{row.codigo_distribuidora || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.distribuidora || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.provincia || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.municipio || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.cnae || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.consumo_anual_estimado != null ? (Number(row.consumo_anual_estimado) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.tipo_autoconsumo || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.fecha_activacion_autoconsumo ? new Date(row.fecha_activacion_autoconsumo).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.tiene_bateria_virtual ? 'Sí' : 'No'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.id_bateria || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.carga_bateria || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.tipo_mandato || '-'}</td>
                      </>
                    )}
                    
                    {/* Tarifa */}
                    {expandedSections.tarifa && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 border-l-4 border-purple-400">{row.id_tarifa_precios || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.motivo_precio || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.tipo_tarifa_precio || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 max-w-xs truncate">{row.descripcion_tarifa || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.alias_externo || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.fee_indexado != null ? Number(row.fee_indexado).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs">
                          {row.esquema_tarifa ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              row.esquema_tarifa === 'extramargen' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                              row.esquema_tarifa === 'fijo' ? 'bg-green-50 text-green-700 border border-green-200' :
                              (row.esquema_tarifa === 'easy' || row.esquema_tarifa?.startsWith('EASY_')) ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                              esquemasVolumenCups.has(row.esquema_tarifa) ? 'bg-teal-50 text-teal-700 border border-teal-200' :
                              'bg-gray-50 text-gray-700 border border-gray-200'
                            }`}>
                              {row.esquema_tarifa}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">Sin tarifa</span>
                          )}
                        </td>
                      </>
                    )}
                    
                    {/* Potencias */}
                    {expandedSections.potencias && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right border-l-4 border-teal-400">{row.potencia_contratada_p1_kw != null ? Number(row.potencia_contratada_p1_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.potencia_contratada_p2_kw != null ? Number(row.potencia_contratada_p2_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.potencia_contratada_p3_kw != null ? Number(row.potencia_contratada_p3_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.potencia_contratada_p4_kw != null ? Number(row.potencia_contratada_p4_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.potencia_contratada_p5_kw != null ? Number(row.potencia_contratada_p5_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.potencia_contratada_p6_kw != null ? Number(row.potencia_contratada_p6_kw).toFixed(2) : '-'}</td>
                      </>
                    )}
                    
                    {/* Márgenes Energía */}
                    {expandedSections.margenes_energia && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right border-l-4 border-orange-400">{row.e1_base != null ? Number(row.e1_base).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e2_base != null ? Number(row.e2_base).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e3_base != null ? Number(row.e3_base).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e4_base != null ? Number(row.e4_base).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e5_base != null ? Number(row.e5_base).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e6_base != null ? Number(row.e6_base).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P1_euros_kw != null ? Number(row.extramargen_P1_euros_kw).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P2_euros_kw != null ? Number(row.extramargen_P2_euros_kw).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P3_euros_kw != null ? Number(row.extramargen_P3_euros_kw).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P4_euros_kw != null ? Number(row.extramargen_P4_euros_kw).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P5_euros_kw != null ? Number(row.extramargen_P5_euros_kw).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P6_euros_kw != null ? Number(row.extramargen_P6_euros_kw).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e1_final != null ? Number(row.e1_final).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e2_final != null ? Number(row.e2_final).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e3_final != null ? Number(row.e3_final).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e4_final != null ? Number(row.e4_final).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e5_final != null ? Number(row.e5_final).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.e6_final != null ? Number(row.e6_final).toFixed(4) : '-'}</td>
                      </>
                    )}
                    
                    {/* Márgenes Potencia */}
                    {expandedSections.margenes_potencia && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right border-l-4 border-pink-400">{row.p1_base != null ? Number(row.p1_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p2_base != null ? Number(row.p2_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p3_base != null ? Number(row.p3_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p4_base != null ? Number(row.p4_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p5_base != null ? Number(row.p5_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p6_base != null ? Number(row.p6_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P1_euros_kw_anio != null ? Number(row.extramargen_P1_euros_kw_anio).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P2_euros_kw_anio != null ? Number(row.extramargen_P2_euros_kw_anio).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P3_euros_kw_anio != null ? Number(row.extramargen_P3_euros_kw_anio).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P4_euros_kw_anio != null ? Number(row.extramargen_P4_euros_kw_anio).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P5_euros_kw_anio != null ? Number(row.extramargen_P5_euros_kw_anio).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.extramargen_P6_euros_kw_anio != null ? Number(row.extramargen_P6_euros_kw_anio).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p1_final != null ? Number(row.p1_final).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p2_final != null ? Number(row.p2_final).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p3_final != null ? Number(row.p3_final).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p4_final != null ? Number(row.p4_final).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p5_final != null ? Number(row.p5_final).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.p6_final != null ? Number(row.p6_final).toFixed(2) : '-'}</td>
                      </>
                    )}
                    
                    {/* Otras Condiciones */}
                    {expandedSections.otras_condiciones && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right border-l-4 border-indigo-400">{row.a1_base != null ? Number(row.a1_base).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.margen_exc != null ? Number(row.margen_exc).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.porc_exc != null ? Number(row.porc_exc).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.anyos || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.meses || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.aplica_limite_servicios_ajustes ? 'Sí' : 'No'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 max-w-xs truncate">{row.observaciones || '-'}</td>
                      </>
                    )}
                    
                    {/* Datos Liquidación */}
                    {expandedSections.liquidacion_datos && (
                      <>
                        <td className="px-1 py-0.5 text-xs text-gray-600 border-l-4 border-cyan-400">{row.liquidacion_tipo || '-'}</td>
                        <td className="px-1 py-0.5 text-xs">
                          {row.esquema_liquidacion ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              row.esquema_liquidacion === 'extramargen' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                              row.esquema_liquidacion === 'fijo' ? 'bg-green-50 text-green-700 border border-green-200' :
                              (row.esquema_liquidacion === 'easy' || row.esquema_liquidacion?.startsWith('EASY_')) ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                              esquemasVolumenCups.has(row.esquema_liquidacion) ? 'bg-teal-50 text-teal-700 border border-teal-200' :
                              'bg-gray-50 text-gray-700 border border-gray-200'
                            }`}>
                              {row.esquema_liquidacion}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">Sin esquema</span>
                          )}
                        </td>
                        <td className="px-1 py-0.5 text-xs">
                          {row.liquidacion_origen_consumo ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              row.liquidacion_origen_consumo === 'SIPS' ? 'bg-green-100 text-green-800' :
                              row.liquidacion_origen_consumo === 'CONTRATO' || row.liquidacion_origen_consumo === 'contrato' ? 'bg-blue-100 text-blue-800' :
                              row.liquidacion_origen_consumo === 'ESTIMADO' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {row.liquidacion_origen_consumo}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_porcentaje_extra_broker != null ? Number(row.liquidacion_porcentaje_extra_broker).toFixed(4) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_comision_emwh != null ? Number(row.liquidacion_comision_emwh).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_comision_ecups != null ? Number(row.liquidacion_comision_ecups).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_cae_p1 != null ? (Number(row.liquidacion_cae_p1) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_cae_p2 != null ? (Number(row.liquidacion_cae_p2) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_cae_p3 != null ? (Number(row.liquidacion_cae_p3) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_cae_p4 != null ? (Number(row.liquidacion_cae_p4) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_cae_p5 != null ? (Number(row.liquidacion_cae_p5) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_cae_p6 != null ? (Number(row.liquidacion_cae_p6) / 1000).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_potencia_contratada_p1_kw != null ? Number(row.liquidacion_potencia_contratada_p1_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_potencia_contratada_p2_kw != null ? Number(row.liquidacion_potencia_contratada_p2_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_potencia_contratada_p3_kw != null ? Number(row.liquidacion_potencia_contratada_p3_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_potencia_contratada_p4_kw != null ? Number(row.liquidacion_potencia_contratada_p4_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_potencia_contratada_p5_kw != null ? Number(row.liquidacion_potencia_contratada_p5_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.liquidacion_potencia_contratada_p6_kw != null ? Number(row.liquidacion_potencia_contratada_p6_kw).toFixed(2) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.liquidacion_fecha_inicio_producto ? new Date(row.liquidacion_fecha_inicio_producto).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">{row.liquidacion_fecha_fin_producto ? new Date(row.liquidacion_fecha_fin_producto).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.dias_incluidos_liquidacion || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900 font-medium text-right" title={row.consumo_anual_estimado ? `${(row.consumo_anual_estimado / 1000000).toFixed(2)} MWh` : ''}>
                          {row.consumo_anual_estimado != null ? (Number(row.consumo_anual_estimado) / 1000).toLocaleString('es', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                        </td>
                      </>
                    )}
                    
                    {/* Liquidación */}
                    {expandedSections.liquidacion && (
                      <>
                        <td className="px-1 py-0.5 text-xs border-l-4 border-green-500">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            row.liquidacion_estado === 'calculada' ? 'bg-green-100 text-green-800' :
                            row.liquidacion_estado === 'pendiente' ? 'bg-amber-100 text-amber-800' :
                            row.liquidacion_estado === 'bloqueada' ? 'bg-red-100 text-red-800' :
                            row.liquidacion_estado === 'no_liquidable' ? 'bg-rose-100 text-rose-800' :
                            row.liquidacion_estado === 'error' ? 'bg-red-200 text-red-900' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {row.liquidacion_estado || '-'}
                          </span>
                        </td>
                        <td className="px-1 py-0.5 text-xs text-red-600 max-w-xs truncate" title={row.liquidacion_error}>{row.liquidacion_error || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-900 font-semibold text-right">
                          {row.liquidacion != null ? `${Number(row.liquidacion).toFixed(2)} €` : '-'}
                        </td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">
                          {row.liquidacion_extramargen_bruto != null ? `${Number(row.liquidacion_extramargen_bruto).toFixed(2)} €` : '-'}
                        </td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">
                          {row.liquidacion_fecha_calculo ? new Date(row.liquidacion_fecha_calculo).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}
                        </td>
                      </>
                    )}
                    
                    {/* Decomisión */}
                    {expandedSections.decomision && (
                      <>
                        <td className="px-1 py-0.5 text-xs border-l-4 border-purple-500">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            row.decomision_estado === 'calculada' ? 'bg-purple-100 text-purple-800' :
                            row.decomision_estado === 'pendiente' ? 'bg-amber-100 text-amber-800' :
                            row.decomision_estado === 'bloqueada' ? 'bg-red-100 text-red-800' :
                            row.decomision_estado === 'error' ? 'bg-red-200 text-red-900' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {row.decomision_estado || '-'}
                          </span>
                        </td>
                        <td className="px-1 py-0.5 text-xs text-red-600 max-w-xs truncate" title={row.decomision_error}>{row.decomision_error || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-purple-900 font-semibold text-right">
                          {row.decomision != null ? `${Number(row.decomision).toFixed(2)} €` : '-'}
                        </td>
                        <td className="px-1 py-0.5 text-xs text-gray-600 text-right">{row.decomision_dias_calculo || '-'}</td>
                        <td className="px-1 py-0.5 text-xs text-gray-600">
                          {row.decomision_fecha_calculo ? new Date(row.decomision_fecha_calculo).toLocaleDateString('es', {day:'2-digit',month:'2-digit',year:'2-digit'}) : '-'}
                        </td>
                      </>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <p className="text-sm mb-1">No se encontraron liquidaciones</p>
            <p className="text-xs">Intenta ajustar los filtros o sincronizar los datos desde BD Origen</p>
          </div>
        )}

        {/* Paginación */}
        {liquidacionesData && liquidacionesData.total_pages > 1 && (
          <div className="shrink-0 px-1 py-2 border-t border-gray-200 bg-white flex items-center justify-between">
            <div className="text-xs text-gray-600">
              Página {liquidacionesData.page} de {liquidacionesData.total_pages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={liquidacionesData.page === 1}
                className="px-2 py-1 border border-gray-300 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={liquidacionesData.page === liquidacionesData.total_pages}
                className="px-2 py-1 border border-gray-300 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Progreso */}
      <ProcessProgressModal
        isOpen={progressModalOpen}
        onClose={handleModalClose}
        jobId={currentJobId}
        processName={currentProcessName}
      />

      {/* Modal de Resumen de Liquidación */}
      <ResumenLiquidacionModal
        isOpen={resumenModalOpen}
        onClose={handleCloseResumen}
        resumen={resumenData}
      />

      {canalParaDesactivar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="desactivar-canal-title"
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="border-b border-gray-100 bg-red-50 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-red-100 p-2 text-red-600">
                  <Ban className="h-5 w-5" />
                </div>
                <div>
                  <h2 id="desactivar-canal-title" className="text-lg font-semibold text-gray-900">
                    Desactivar canal
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Las nuevas liquidaciones de este canal quedarán bloqueadas.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700">
                ¿Quieres añadir <strong>{canalParaDesactivar}</strong> a la lista de canales desactivados?
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCanalParaDesactivar(null)}
                  disabled={desactivarCanalMutation.isPending}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => desactivarCanalMutation.mutate(canalParaDesactivar)}
                  disabled={desactivarCanalMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {desactivarCanalMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Sí, desactivar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiquidacionesPage;
